import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CachedImage from "../CachedImage";
import { getArticleCode } from "../../utils/articleCode";
import {
  getPurchaseCatalogCostPrice,
  getPurchaseCatalogMrpPrice,
} from "../../utils/purchaseCatalogPricing";

const formatAmount = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function ProductTableRow({
  row,
  cartLine,
  priceOverride,
  onAdd,
  onPriceConfig,
  disabled,
}) {
  const [qtyInput, setQtyInput] = useState(
    cartLine ? String(cartLine.quantity) : "0",
  );
  useEffect(() => {
    if (cartLine) {
      setQtyInput(String(cartLine.quantity));
    } else {
      setQtyInput("0");
    }
  }, [cartLine]);

  const articleCode = getArticleCode(row);
  const imageUrl = row.images?.[0];
  const available = Number(row.available_quantity) || 0;
  const outOfStock = row.out_of_stock || available < 1;
  const inCart = Boolean(cartLine);
  const mrpPrice =
    priceOverride?.unit_price != null
      ? Number(priceOverride.unit_price)
      : getPurchaseCatalogMrpPrice(row);
  const costPrice =
    priceOverride?.unit_cost != null
      ? Number(priceOverride.unit_cost)
      : getPurchaseCatalogCostPrice(row);
  const hasCustomPrice = Boolean(priceOverride);
  const orderedQty = cartLine ? Number(cartLine.quantity) || 0 : 0;
  const overAvailable = inCart && orderedQty > available;

  const syncToCart = (rawQty) => {
    const qty = Math.floor(Number(rawQty));
    if (!Number.isFinite(qty) || qty < 1) return;
    setQtyInput(String(qty));
    onAdd(row, qty);
  };

  const handlePlus = () => {
    const current = Math.max(0, Math.floor(Number(qtyInput)) || 0);
    if (inCart) {
      syncToCart(current + 1);
    } else {
      syncToCart(current < 1 ? 1 : current);
    }
  };

  const handleMinus = () => {
    const current = Math.max(0, Math.floor(Number(qtyInput)) || 0);
    if (!inCart || current <= 1) return;
    syncToCart(current - 1);
  };

  const handleQtyBlur = () => {
    if (!inCart) return;
    syncToCart(qtyInput);
  };

  return (
    <TableRow hover>
      <TableCell sx={{ width: 72 }}>
        <CachedImage
          src={imageUrl}
          alt={row.product_name}
          size={48}
          variant="rounded"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.product_name}
        </Typography>
        {inCart && (
          <Chip
            label="In order"
            size="small"
            color="success"
            variant="outlined"
            sx={{ mt: 0.5, mr: 0.5 }}
          />
        )}
        {hasCustomPrice && (
          <Chip
            label="Custom price"
            size="small"
            color="info"
            variant="outlined"
            sx={{ mt: 0.5, mr: 0.5 }}
          />
        )}
        {overAvailable && (
          <Chip
            label="Qty exceeds stock"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        )}
      </TableCell>
      <TableCell>{articleCode || row.sku || "—"}</TableCell>
      <TableCell>
        {row.delivery_estimate
          ? `${row.delivery_estimate.min}–${row.delivery_estimate.max} ${row.delivery_estimate.unit}`
          : "—"}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatAmount(costPrice)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatAmount(mrpPrice)}</Typography>
      </TableCell>
      <TableCell align="center">
        <Button
          size="small"
          variant="text"
          onClick={() => onPriceConfig?.(row)}
          disabled={disabled}
          sx={{ color: "#e90000", textTransform: "none", minWidth: 72 }}
        >
          Configure
        </Button>
      </TableCell>
      <TableCell align="right">
        {outOfStock ? (
          <Chip
            label="Out of stock"
            size="small"
            color="warning"
            variant="outlined"
          />
        ) : (
          available
        )}
      </TableCell>
      <TableCell align="right" sx={{ width: 148 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.25,
            justifyContent: "flex-end",
          }}
        >
          <IconButton
            size="small"
            onClick={handleMinus}
            disabled={disabled || !inCart}
            aria-label="Decrease quantity"
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <TextField
            size="small"
            type="number"
            inputProps={{ min: inCart ? 1 : 0, step: 1 }}
            value={qtyInput}
            onChange={(e) => setQtyInput(e.target.value)}
            onBlur={handleQtyBlur}
            disabled={disabled}
            sx={{ width: 56, "& input": { textAlign: "center", py: 0.75 } }}
          />
          <IconButton
            size="small"
            onClick={handlePlus}
            disabled={disabled}
            aria-label="Increase quantity"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
}
